import React from 'react';
import dayjs from 'dayjs';
import { Badge } from '@/design-system/badge';
import {
  COD_BUCKET_LABELS,
  COD_SETTLEMENT_SOURCE_LABELS,
  type CodBucket,
  type CodSettlementSource,
  codMethodLabel,
} from '@/services/api/codOrders';

/** `cod_status` on the wire → the bucket the UI groups it under. */
export const bucketOfStatus = (status: string | null): CodBucket =>
  (status as CodBucket | null) ?? 'in_delivery';

type BadgeStatus = React.ComponentProps<typeof Badge>['status'];

const BUCKET_TONE: Record<CodBucket, BadgeStatus> = {
  in_delivery: 'information',
  outstanding: 'warning',
  pending_deposit: 'warning',
  // Waiting on Finance, not on Ops — the same "someone still owes an action"
  // tone as the two before it, because from the board's point of view it is
  // exactly that.
  pending_finance: 'warning',
  confirmed: 'success',
  // Not 'faded' like `not_applicable`: this order is not closed, it is between
  // attempts, and greying it out would read as "nothing more will happen here".
  failed_delivery: 'error',
  not_applicable: 'faded',
};

/**
 * Where the money is. Deliberately not coloured by urgency — that would need an
 * ageing threshold nobody has agreed (the PRD's 24h is contradicted by a real
 * median of 31.1h). The two buckets that still owe an action share one tone;
 * the bucket cards and the Time in Status column are what prioritise within
 * them.
 */
export const CodStatusBadge: React.FC<{ status: string | null }> = ({
  status,
}) => {
  const bucket = bucketOfStatus(status);
  return (
    <Badge status={BUCKET_TONE[bucket]}>{COD_BUCKET_LABELS[bucket]}</Badge>
  );
};

/**
 * Marks a confirmation with no proof document behind it.
 *
 * Reads the document's presence directly. Proof used to be mandatory, so "has
 * no proof" could be inferred from where the confirmation came from; since it
 * became optional (2026-09-02) the file's absence is the only fact that says
 * so, and it is what Finance needs to see.
 */
export const CodProofBadge: React.FC<{ hasProof: boolean }> = ({
  hasProof,
}) => {
  if (hasProof) return null;
  return (
    <Badge
      status="faded"
      title="Dikonfirmasi tanpa melampirkan bukti pembayaran"
    >
      Tanpa bukti
    </Badge>
  );
};

/**
 * The three stage columns from the mockup — Confirmation, Deposit, Resolved —
 * but showing WHAT happened at each stage rather than only which stage the row
 * currently occupies.
 *
 * The mockup drops a single status pill into the one column matching the row's
 * stage and dashes the rest. That reads as a progress bar, which is useful, but
 * it throws away the outcome: a rider who paid, a PIC who fronted the cash, and
 * a tribe write-off all sit in the same stage and are the same pill. Those are
 * three different accounting events, and telling them apart is the reason
 * `settlement_source` is stored at all.
 *
 * `Menunggu` = this stage has not happened yet. `—` = it never will, because
 * the order left the flow (returned/failed) or never entered it.
 */
/**
 * `text-sub-600`, not `soft-400`. The soft token measures 2.35:1 on white — it
 * fails WCAG AA (4.5:1) and reads as disabled text, which is the wrong signal
 * for a stage that is genuinely still pending. Every label on this screen moved
 * off it for the same reason (design audit 2026-09-03).
 */
const StagePending: React.FC = () => (
  <span className="text-xs text-text-sub-600">Menunggu</span>
);

const StageEmpty: React.FC<{ title: string }> = ({ title }) => (
  <span className="text-xs text-text-sub-600" title={title}>
    —
  </span>
);

/** Whether a row can still move through the custody stages at all. */
const isTerminalNonCollected = (status: string | null) =>
  status === 'not_applicable';

/**
 * Leg 1: how the cash got from the rider to the PIC, plus the two flags that
 * are facts ABOUT that confirmation — whether it carries a proof document, and
 * whether the amount disagreed with what was owed.
 *
 * Those two used to sit in the Status column, beside the bucket badge. They
 * belong here: Status answers "where is this row", these answer "is the
 * confirmation trustworthy", and keeping them together is also what stops the
 * Status column being three badges wide.
 */
export const CodConfirmationCell: React.FC<{
  row: {
    status: string | null;
    settlementSource: CodSettlementSource | null;
    discrepancyLeg1: number | null;
    leg1HasProof: boolean | null;
  };
}> = ({ row }) => {
  if (row.settlementSource) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <CodSettlementBadge method={row.settlementSource} />
        {/* Leg 1's OWN proof and discrepancy. Reading the merged row-level
            flags put a Leg 2 shortfall on this column, which blames the rider
            for what the PIC did. */}
        <CodProofBadge hasProof={row.leg1HasProof !== false} />
        <StageDiscrepancy
          amount={row.discrepancyLeg1}
          against="jumlah COD yang seharusnya ditagih"
        />
      </div>
    );
  }
  if (isTerminalNonCollected(row.status)) {
    return (
      <StageEmpty title="Retur / gagal kirim — tidak ada uang yang ditagih" />
    );
  }
  // Includes the in-delivery bucket: nothing is owed yet, but this stage is
  // still ahead of the row, so "Menunggu" is accurate.
  return <StagePending />;
};

/** A leg's own discrepancy, with the direction and the baseline spelled out. */
const StageDiscrepancy: React.FC<{
  amount: number | null;
  against: string;
}> = ({ amount, against }) => {
  if (amount == null || Math.round(amount) === 0) return null;
  return (
    <span
      className="text-xs font-medium text-state-warning-dark"
      title={`${formatDiscrepancy(amount)} terhadap ${against}`}
    >
      {formatDiscrepancy(amount)}
    </span>
  );
};

/** Leg 2: whether the PIC banked it, and whether the amount matched. */
export const CodDepositCell: React.FC<{
  row: {
    status: string | null;
    depositMethod: string | null;
    discrepancyLeg2: number | null;
    leg2HasProof: boolean | null;
  };
}> = ({ row }) => {
  if (row.depositMethod) {
    // The method itself is a single value today (`bank_transfer`), so the
    // useful fact is that it happened — the method is in the tooltip.
    return (
      <div className="flex flex-wrap items-center gap-1">
        <Badge status="success" title={codMethodLabel(row.depositMethod)}>
          Disetor
        </Badge>
        <CodProofBadge hasProof={row.leg2HasProof !== false} />
        <StageDiscrepancy
          amount={row.discrepancyLeg2}
          against="jumlah yang diterima PIC di Leg 1"
        />
      </div>
    );
  }
  if (isTerminalNonCollected(row.status)) {
    return <StageEmpty title="Retur / gagal kirim — tidak ada setoran" />;
  }
  return <StagePending />;
};

/**
 * Terminal: the money is with Finance — and whether all of it got there.
 *
 * The badge alone said "closed", not "closed whole". The net is measured
 * END TO END, deposited against the original COD amount, because the per-leg
 * discrepancies do not answer the question on their own: a rider who handed
 * over Rp 10.000 short and a PIC who covered it out of pocket produce a −10.000
 * and a +10.000 that cancel, and the company is not owed anything. The same
 * two legs without that top-up leave the order Rp 10.000 short even though
 * Leg 2 itself reconciled perfectly against Leg 1.
 */
export const CodResolvedCell: React.FC<{
  row: {
    status: string | null;
    resolvedAt: string | null;
    amount: number;
    depositedAmount: number | null;
  };
}> = ({ row }) => {
  if (row.status === 'confirmed' && row.resolvedAt) {
    const net =
      row.depositedAmount != null
        ? Math.round(row.depositedAmount) - Math.round(row.amount)
        : null;
    const settled = net != null && net === 0;
    return (
      <div className="flex flex-wrap items-center gap-1">
        <Badge status="success" title={formatDateTime(row.resolvedAt)}>
          Selesai
        </Badge>
        {net == null ? null : settled ? (
          <span
            className="text-xs text-text-sub-600"
            title={`${formatRupiah(row.depositedAmount)} dari ${formatRupiah(row.amount)} — utuh`}
          >
            Utuh
          </span>
        ) : (
          <span
            className="text-xs font-medium text-state-warning-dark"
            title={`${formatRupiah(row.depositedAmount)} dari ${formatRupiah(row.amount)} — ${
              net < 0 ? 'kurang' : 'lebih'
            } ${formatRupiah(Math.abs(net))}`}
          >
            {formatDiscrepancy(net)}
          </span>
        )}
      </div>
    );
  }
  if (isTerminalNonCollected(row.status)) {
    // Terminal but NOT resolved — no money ever moved. Calling this "Selesai"
    // would let a returned order read as a completed collection.
    return <Badge status="faded">Tidak Berlaku</Badge>;
  }
  return <StagePending />;
};

export const CodSettlementBadge: React.FC<{ method: string | null }> = ({
  method,
}) => {
  if (!method) return <span className="text-text-sub-600">—</span>;
  const label =
    COD_SETTLEMENT_SOURCE_LABELS[method as CodSettlementSource] ?? method;
  // A write-off is an accounting event, not a collection — worth reading
  // differently at a glance from a rider who actually paid.
  const status: BadgeStatus = method === 'rider_paid' ? 'success' : 'warning';
  return <Badge status={status}>{label}</Badge>;
};

/** Rupiah, no decimals — every COD figure in production is a whole Rupiah. */
export const formatRupiah = (value: number | null | undefined): string =>
  value == null ? '—' : `Rp ${Math.round(value).toLocaleString('id-ID')}`;

/** Signed Rupiah: a discrepancy reads better with its direction explicit. */
export const formatDiscrepancy = (value: number): string => {
  if (value === 0) return 'Sesuai';
  const sign = value > 0 ? '+' : '−';
  return `${sign} Rp ${Math.abs(Math.round(value)).toLocaleString('id-ID')}`;
};

export const formatDateTime = (value?: string | null): string =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

/**
 * How long this order has been in its current custody stage.
 *
 * This is the list's prioritisation signal, and it is shown as an elapsed
 * duration rather than a red/amber tier on purpose: the tiers need a threshold
 * Ops and Finance have not agreed, and the number the PRD assumed would paint
 * more than half the list red on day one. A duration lets the reader judge.
 */
export const formatTimeInStatus = (
  stageStartedAt: string | null,
  status?: string | null
): string => {
  // Terminal rows have no running stage, so an elapsed duration is not just
  // useless — it is wrong. `confirmed` money is with Finance and
  // `not_applicable` was never collected; a number that keeps growing on
  // either says "this has been waiting 14 days" about an order nobody is
  // waiting on, and buries the rows that genuinely are.
  if (status === 'confirmed') return 'Selesai';
  if (status === 'not_applicable') return 'Tidak Berlaku';
  if (!stageStartedAt) return '—';
  const start = dayjs(stageStartedAt);
  if (!start.isValid()) return '—';

  const minutes = dayjs().diff(start, 'minute');
  if (minutes < 60) return `${Math.max(minutes, 0)} mnt`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam`;

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days} hari` : `${days}h ${restHours}j`;
};
